/**
 * Simplify and flatten user permissions and other client/user data for easy access
 *
 *  r.tierLite       = lite       = c.tier === 0;
 *  r.tierPro        = pro        = c.tier === 1;
 *  r.tierEnterprise = enterprise = c.tier === 2;
 *
 * @typedef {Object} Permissions
 * @property {boolean} downloadSchedule - Whether the user has permission to download schedules.
 * @property {boolean} atLeastTierProOrSuperUser - Whether the user's client is at least Tier Pro or the user is a superuser.
 * @property {boolean} atLeastTierPro - Whether the user's client is at least Tier Pro.
 * @property {boolean} atLeastTierEnterprise - Whether the user's client is at least Tier Enterprise.
 * @property {string} tierText - A human-readable representation of the client's tier (e.g., 'Lite', 'Pro', 'Enterprise').
 * @property {boolean} superuser - Whether the user is a superuser.
 * @property {boolean} reader - Whether the user has Reader permissions.
 * @property {boolean} contentViewer - Whether the user has Content Viewer permissions.
 * @property {boolean} writer - Whether the user has Writer permissions.
 * @property {boolean} approver - Whether the user has Approver permissions.
 * @property {boolean} deployer - Whether the user has Deployer permissions.
 * @property {boolean} assigner - Whether the user has Assigner permissions.
 * @property {boolean} admin - Whether the user has Admin permissions.
 * @property {boolean} locker - Whether the user has Locker permissions.
 * @property {boolean} commander - Whether the user has Commander permissions.
 * @property {boolean} deviceViewer - Whether the user has Device Viewer permissions.
 * @property {boolean} deviceManager - Whether the user has Device Manager permissions.
 * @property {boolean} hasModules - Whether the user's client has any modules enabled.
 * @property {boolean} moduleDataObject - Whether the user's client has the Data Object module enabled.
 * @property {boolean} moduleUrlRuleset - Whether the user's client has the URL Ruleset module enabled.
 * @property {string|null} landingpage - The landing page for the client's users, or null if not
 *
 * @return {Permissions} An object containing the user's permissions and related client/user information.
 */
export const getPermissions = auth => {
  const { user } = auth || {};

  if (!user) {
    return {};
  }

  const { client: c, user: u, permissions: p, Modules: m } = user;
  const r = {};
  const su = Boolean(u.superuser);

  r.superuser = su;

  r.atLeastTierProOrSuperUser = c.tier > 0 || su;
  r.atLeastTierPro = c.tier > 0;
  r.atLeastTierEnterprise = c.tier > 1;

  const suText = su ? '(superuser)' : '';

  r.tierText = [`${suText}Lite`, `${suText}Pro`, `${suText}Enterprise`][c.tier];

  r.downloadSchedule = Boolean(user.downloadSchedule);

  r.reader = p.Reader;
  r.contentViewer = p['Content Viewer'];
  r.writer = p.Writer;
  r.approver = p.Approver;
  r.deployer = p.Deployer;
  r.assigner = p.Assigner;
  r.admin = p.Admin;
  r.locker = p.Locker;
  r.commander = p.Commander;
  r.deviceViewer = p['Device Viewer'];
  r.deviceManager = p['Device Manager'];

  r.hasModules = m.length > 0;
  r.moduleDataObject = m.some(mod => mod.name === 'DataObject');
  r.moduleUrlRuleset = m.some(mod => mod.name === 'UrlRuleset');

  r.landingpage = c.landingpage || null;

  return r;
};
